import React, { useRef, useEffect } from 'react';

/**
 * AudioReactiveHero — canvas replacement for the static CSS waveform bar grid
 * on the landing page hero. Draws a layered, pulsating spectrum-style bar chart
 * that reacts to mouse position (x = spectral phase, y = intensity) using
 * pure sine/cosine synthesis — no actual audio input needed, so it works before
 * any user gesture and never requests microphone permission.
 *
 * Why canvas and not CSS: 60+ animated DOM nodes cause layout jank on mobile;
 * a single canvas repaint is ~1ms on a mid-range phone.
 */
const AudioReactiveHero = ({ barCount = 72, height = 112 }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const pointerRef = useRef({ x: 0.5, y: 0.5, active: false });
  const startRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let width = 0;
    let h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      h = rect.height;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      pointerRef.current.x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      pointerRef.current.y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
      pointerRef.current.active = true;
    };
    const onLeave = () => { pointerRef.current.active = false; };
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerleave', onLeave);

    startRef.current = performance.now();

    const draw = (now) => {
      const t = (now - startRef.current) / 1000; // seconds
      ctx.clearRect(0, 0, width, h);

      const { x: px, y: py, active } = pointerRef.current;
      // Pointer idle → drift toward center
      const targetX = active ? px : 0.5 + 0.15 * Math.sin(t * 0.3);
      const intensity = active ? 0.55 + py * 0.6 : 0.75;

      const gap = 3;
      const available = width - gap * (barCount - 1);
      const bw = Math.max(1.5, available / barCount);
      const midY = h / 2;

      // Pre-build gradient once per frame
      const grad = ctx.createLinearGradient(0, 0, 0, h);
      grad.addColorStop(0, 'rgba(6,182,212,0.9)');   // cyan top
      grad.addColorStop(0.5, 'rgba(139,92,246,0.85)'); // purple middle
      grad.addColorStop(1, 'rgba(6,182,212,0.9)');   // cyan bottom mirror

      ctx.fillStyle = grad;

      for (let i = 0; i < barCount; i++) {
        const n = i / (barCount - 1); // 0..1 across bar index
        // Distance from pointer's spectral position, used as a soft spotlight
        const d = Math.abs(n - targetX);
        const spotlight = Math.exp(-d * d * 14); // gaussian peak at targetX

        // Three harmonics — primary (slow), mid, fast — produce an organic EEG feel
        const h1 = Math.sin(t * 1.6 + n * 6.28);
        const h2 = Math.sin(t * 3.1 + n * 11.0 + 1.3);
        const h3 = Math.cos(t * 0.9 + n * 3.7);
        const wave = (h1 * 0.55 + h2 * 0.3 + h3 * 0.35);

        // Base 18%, waves add up to ~75%, spotlight pushes hot bars toward 100%
        const amp = 0.18 + 0.32 * (wave * 0.5 + 0.5) * intensity + 0.45 * spotlight;
        const barH = Math.max(3, amp * h * 0.9);

        const x = i * (bw + gap);
        // Mirror vertical bar around center line
        const yTop = midY - barH / 2;

        // Soft rounded bars via path (faster than roundRect on older Safari)
        const r = Math.min(bw / 2, 3);
        ctx.beginPath();
        ctx.moveTo(x + r, yTop);
        ctx.lineTo(x + bw - r, yTop);
        ctx.quadraticCurveTo(x + bw, yTop, x + bw, yTop + r);
        ctx.lineTo(x + bw, yTop + barH - r);
        ctx.quadraticCurveTo(x + bw, yTop + barH, x + bw - r, yTop + barH);
        ctx.lineTo(x + r, yTop + barH);
        ctx.quadraticCurveTo(x, yTop + barH, x, yTop + barH - r);
        ctx.lineTo(x, yTop + r);
        ctx.quadraticCurveTo(x, yTop, x + r, yTop);
        ctx.closePath();
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerleave', onLeave);
    };
  }, [barCount]);

  return (
    <div
      className="relative max-w-3xl mx-auto"
      style={{ height }}
      data-testid="audio-reactive-hero"
    >
      <canvas
        ref={canvasRef}
        className="w-full h-full block cursor-crosshair"
        aria-label="Animated brainwave visualization — hover to interact"
      />
      {/* Subtle glow underneath */}
      <div
        className="absolute inset-x-0 bottom-0 h-6 pointer-events-none"
        style={{
          background: 'radial-gradient(60% 100% at 50% 100%, rgba(139,92,246,0.35), transparent 70%)',
          filter: 'blur(14px)',
        }}
      />
    </div>
  );
};

export default AudioReactiveHero;
